import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Page from "../../organisms/Page";
import { BreadCrumbItem } from "../../organisms/HeaderSection";
import LoadingScreen from "../../organisms/LoadingScreen";
import ServerErrorModal from "../../organisms/ServerErrorModal";
import ValidationError, {
  ValidationErrorResponse,
} from "../../../services/ValidationError";
import {
  NuevoGrupoUsuario,
  QUERY_LEER_GRUPOS_USUARIOS,
  QUERY_GRUPO_USUARIO_CONTIENE_USUARIOS,
  actualizarGrupoUsuario,
  crearGrupoUsuario,
  eliminarGrupoUsuario,
  useGetGruposUsuarios,
  useGetRoles,
} from "../../../services/seguridadServicios";
import GruposUsuariosSection from "./GruposUsuariosSection";

const breadCrumbs: BreadCrumbItem[] = [
  { path: "/", label: "Inicio" },
  { path: "/seguridad", label: "Seguridad" },
  { path: "/seguridad/asignar-roles", label: "Asignar roles", isActive: true },
];

interface ActionResult {
  validationErrors?: ValidationErrorResponse;
}

function AsignarRoles() {
  const queryClient = useQueryClient();
  const [serverErrorOcurrio, setServerErrorOcurrio] = useState(false);
  const [idGrupoGuardando, setIdGrupoGuardando] = useState<string | undefined>(
    undefined
  );
  const [idGrupoEliminando, setIdGrupoEliminando] = useState<string | undefined>(
    undefined
  );

  const {
    data: grupos,
    isLoading: gruposIsLoading,
    error: gruposError,
  } = useGetGruposUsuarios();
  const { data: roles, isLoading: rolesIsLoading, error: rolesError } =
    useGetRoles();

  const crearGrupoMutation = useMutation({
    mutationFn: crearGrupoUsuario,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_LEER_GRUPOS_USUARIOS],
      });
    },
  });

  const guardarGrupoMutation = useMutation({
    mutationFn: actualizarGrupoUsuario,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_LEER_GRUPOS_USUARIOS],
      });
    },
  });

  const eliminarGrupoMutation = useMutation({
    mutationFn: eliminarGrupoUsuario,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_LEER_GRUPOS_USUARIOS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_GRUPO_USUARIO_CONTIENE_USUARIOS],
      });
    },
  });

  const onCrearGrupo = async (body: NuevoGrupoUsuario): Promise<ActionResult> => {
    try {
      await crearGrupoMutation.mutateAsync(body);
      return {};
    } catch (error: unknown) {
      if (error instanceof ValidationError) {
        return {
          validationErrors: error.getFailedValidations(),
        };
      }
      setServerErrorOcurrio(true);
      return {};
    }
  };

  const onGuardarGrupo = async ({
    idGrupoUsuario,
    body,
  }: {
    idGrupoUsuario: string;
    body: NuevoGrupoUsuario;
  }): Promise<ActionResult> => {
    setIdGrupoGuardando(idGrupoUsuario);
    try {
      await guardarGrupoMutation.mutateAsync({
        id: idGrupoUsuario,
        body,
      });
      return {};
    } catch (error: unknown) {
      if (error instanceof ValidationError) {
        return {
          validationErrors: error.getFailedValidations(),
        };
      }
      setServerErrorOcurrio(true);
      return {};
    } finally {
      setIdGrupoGuardando(undefined);
    }
  };

  const onEliminarGrupo = async (idGrupoUsuario: string) => {
    setIdGrupoEliminando(idGrupoUsuario);
    try {
      await eliminarGrupoMutation.mutateAsync(idGrupoUsuario);
    } catch (_error) {
      setServerErrorOcurrio(true);
    } finally {
      setIdGrupoEliminando(undefined);
    }
  };

  if (gruposError || rolesError) {
    return <ServerErrorModal />;
  }

  if (gruposIsLoading || rolesIsLoading) {
    return <LoadingScreen isLoading={true} />;
  }

  return (
    <Page
      title="Asignar roles"
      description="Designa roles de permisos a grupos de usuarios."
      breadCrumbs={breadCrumbs}
    >
      {serverErrorOcurrio && (
        <ServerErrorModal onClose={() => setServerErrorOcurrio(false)} />
      )}

      <GruposUsuariosSection
        grupos={grupos || []}
        roles={roles || []}
        estaCreandoGrupo={crearGrupoMutation.isLoading}
        idGrupoGuardando={idGrupoGuardando}
        idGrupoEliminando={idGrupoEliminando}
        onCrearGrupo={onCrearGrupo}
        onGuardarGrupo={onGuardarGrupo}
        onEliminarGrupo={onEliminarGrupo}
      />
    </Page>
  );
}

export default AsignarRoles;
